import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  Group,
  Stack,
  Table,
  Title,
  Anchor,
} from "@mantine/core";

import {
  ADMIN_ORGANIZATION,
  CHAMPIONSHIP_CREATE_AUTHORISED_ORGANIZATIONS,
  CHAMPIONSHIP_CREATE_AUTHORISED_ROLES,
  CHAMPIONSHIP_ORGANIZATION_VISIBILITY_AUTHORISED_ORGANIZATIONS,
  CHAMPIONSHIP_ORGANIZATION_VISIBILITY_AUTHORISED_ROLES,
} from "_roles";

import type { Championship } from "championship/types/championship.model";
import { AccessRights } from "common/admin/auth/AccessRights";
import { useOrganization } from "common/admin/auth/hooks";
import QueryWrapper from "common/components/QueryWrapper";
import { Th } from "common/components/table/Th";

import {
  useListChampionshipsQuery,
  useRemoveChampionshipMutation,
} from "championship/api/championship.api";

import { ChampionshipAdminRow } from "../components/ChampionshipAdminRow";
import { ADMIN_PATH } from "_urls";

type SortedKeys = keyof Pick<Championship, "owner" | "name" | "createdAt">;

function sortData(
  data: Championship[],
  payload: { sortBy: SortedKeys; reversed: boolean },
): Championship[] {
  return [...data].sort((a, b) => {
    if (payload.reversed) {
      return String(b[payload.sortBy]).localeCompare(String(a[payload.sortBy]));
    }
    return String(a[payload.sortBy]).localeCompare(String(b[payload.sortBy]));
  });
}

export function ChampionshipsListAdminView() {
  const navigate = useNavigate();
  const listChampionshipsQuery = useListChampionshipsQuery();
  const [sortedData, setSortedData] = useState<Championship[]>([]);
  const [sortBy, setSortBy] = useState<SortedKeys>("createdAt");
  const [reverseSortDirection, setReverseSortDirection] = useState(false);
  const [organization] = useOrganization();

  const championships = useMemo(
    () => listChampionshipsQuery.data,
    [listChampionshipsQuery.data],
  );
  const [deleteChampionship] = useRemoveChampionshipMutation();

  const handleDelete = async (id: string) => {
    try {
      await deleteChampionship(id).unwrap();
      console.log(`Championship ${id} deleted successfully`);
    } catch (error) {
      console.error("Failed to delete championship", error);
    }
  };

  const triggerSortingData = useCallback(
    (field: SortedKeys) => {
      if (championships) {
        const reversed = field === sortBy ? !reverseSortDirection : false;
        return sortData(championships, { sortBy: field, reversed });
      }
      return [];
    },
    [championships, reverseSortDirection, sortBy],
  );

  useEffect(() => {
    if (championships) {
      setSortedData(triggerSortingData(sortBy));
    }
  }, [championships, sortBy, triggerSortingData]);

  return (
    <Box>
      <div
        className="sticky top-0 z-50 pb-4"
        style={{ backgroundColor: "#E9ECEF" }}
      >
        <Title order={3} align="center" m="xs">
          Championships
        </Title>
        <Breadcrumbs separator="→">
          <Anchor component={Link} to={`/${ADMIN_PATH}`}>
            Admin
          </Anchor>
          <span>Championships</span>
        </Breadcrumbs>

        <AccessRights
          authorizedOrganizations={CHAMPIONSHIP_CREATE_AUTHORISED_ORGANIZATIONS}
          authorizedRoles={CHAMPIONSHIP_CREATE_AUTHORISED_ROLES}
        >
          <Group position="right">
            <Button className="bg-button-admin" component={Link} to="new">
              New
            </Button>
          </Group>
        </AccessRights>
      </div>

      <Card shadow={"sm"} p={"xl"} className={"overflow-visible"}>
        <QueryWrapper query={listChampionshipsQuery}>
          {championships ? (
            <Table highlightOnHover>
              <thead>
                <tr>
                  <Th
                    reversed={reverseSortDirection}
                    sorted={sortBy === "name"}
                    onClick={() => {
                      if (sortBy === "name") {
                        setReverseSortDirection(!reverseSortDirection);
                      } else {
                        setSortBy("name");
                      }
                    }}
                  >
                    Name
                  </Th>
                  <AccessRights
                    authorizedOrganizations={
                      CHAMPIONSHIP_ORGANIZATION_VISIBILITY_AUTHORISED_ORGANIZATIONS
                    }
                    authorizedRoles={
                      CHAMPIONSHIP_ORGANIZATION_VISIBILITY_AUTHORISED_ROLES
                    }
                  >
                    <Th
                      reversed={reverseSortDirection}
                      sorted={sortBy === "owner"}
                      onClick={() => {
                        if (sortBy === "owner") {
                          setReverseSortDirection(!reverseSortDirection);
                        } else {
                          setSortBy("owner");
                        }
                      }}
                    >
                      Organization
                    </Th>
                  </AccessRights>
                  <Th
                    reversed={reverseSortDirection}
                    sorted={sortBy === "createdAt"}
                    onClick={() => {
                      if (sortBy === "createdAt") {
                        setReverseSortDirection(!reverseSortDirection);
                      } else {
                        setSortBy("createdAt");
                      }
                    }}
                  >
                    Creation Date
                  </Th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((championship) =>
                  championship.owner.L1 === organization ||
                  organization === ADMIN_ORGANIZATION ? (
                    <ChampionshipAdminRow
                      key={championship.id}
                      championship={championship}
                      onEdit={() => navigate(championship.id)}
                      onDelete={() => handleDelete(championship.id)}
                      onDuplicate={() =>
                        navigate(`${championship.id}/duplicate`)
                      }
                    />
                  ) : null,
                )}
              </tbody>
            </Table>
          ) : (
            <Stack align="center">
              <Box>No championships found</Box>
              <Button className="bg-button-admin" component={Link} to="new">
                New
              </Button>
            </Stack>
          )}
        </QueryWrapper>
      </Card>
    </Box>
  );
}
